/**
 * Hotfix 1.104.1 — DataTables "print" button crashes Chrome/Edge.
 *
 * The stock `buttons.print.js` (datatables.net-buttons v2.0.1) renders the
 * print view by opening an `about:blank` popup with `window.open("", "")`,
 * injecting the table markup through `innerHTML`, then calling `win.print()`
 * followed by `win.close()`. Recent Chromium releases kill the renderer on that
 * exact path, surfacing as `RESULT_CODE_KILLED_BAD_MESSAGE` (the "Can't open
 * this page" / closed-tab the customer started seeing after a Chrome update).
 * It is a Chrome regression, but the fix has to live in our code.
 *
 * This module overrides the print button's `action` so the same markup is
 * rendered into a hidden <iframe> and that browsing context is printed instead
 * — no `window.open`, no popup. It is the technique used by react-to-print and
 * is unaffected by the regression. Importing this module once (from
 * NewDataTable) patches every screen that relies on `extend: "print"`.
 *
 * The markup-building logic is kept faithful to the upstream action so the
 * printed output (columns, classes, header/footer, title, messages, styles)
 * stays identical — only the rendering surface changed.
 */

// region Third-party libraries
import $ from "jquery";
import "datatables.net";
import "datatables.net-buttons";
import "datatables.net-buttons/js/buttons.print.js";
// endregion

// region Types — internals the print action touches that @types omits
type PrintAction = DataTables.FunctionButtonAction;

interface PrintButtonDefinition {
  action: PrintAction;
}

interface DataTableStaticWithPrint {
  ext: { buttons: { print?: PrintButtonDefinition } };
}

interface ColumnInternalSettings {
  sClass: string;
}

interface DataTableSettingsInternal {
  aoColumns: ColumnInternalSettings[];
}

const HIDDEN_IFRAME_ID = "dt-print-iframe";
// Mirrors the upstream "allow stylesheets time to load" delay before printing.
const STYLE_LOAD_DELAY_MS = 1000;
// endregion

// Resolves a relative URL to an absolute one using the host document, so styles
// and images keep working inside the iframe (which has no base URL of its own).
const urlResolver = document.createElement("a");
const toAbsoluteUrl = (href: string): string => {
  urlResolver.href = href;

  return urlResolver.href;
};

// Clone a <style>/<link> node, rewriting a <link>'s href to an absolute URL.
const styleNodeToAbsolute = (element: HTMLElement): string => {
  const clone = $(element).clone()[0] as HTMLElement;

  if (clone.nodeName.toLowerCase() === "link") {
    const link = clone as HTMLLinkElement;

    link.href = toAbsoluteUrl(link.href);
  }

  return clone.outerHTML;
};

const printViaIframe: PrintAction = (e, dt, button, config): void => {
  const printConfig = config as DataTables.ButtonSettings;
  const exportOptions = (printConfig.exportOptions ?? {}) as DataTables.ButtonExportOptions;

  const data = dt.buttons.exportData($.extend({ decodeEntities: false }, exportOptions));
  const exportInfo = dt.buttons.exportInfo(printConfig);

  // Per-column classes (e.g. "print", text alignment) preserved on print cells.
  const columnClasses = dt
    .columns(exportOptions.columns)
    .flatten()
    .map((columnIndex: number) => {
      const settings = dt.settings()[0] as unknown as DataTableSettingsInternal;

      return settings.aoColumns[dt.column(columnIndex).index()].sClass;
    })
    .toArray() as string[];

  const addRow = (cells: Array<string | null>, tag: "th" | "td"): string => {
    let row = "<tr>";

    for (let i = 0; i < cells.length; i += 1) {
      // null/undefined aren't useful in the print output.
      const cell = cells[i] === null || cells[i] === undefined ? "" : cells[i];
      const classAttr = columnClasses[i] ? ` class="${columnClasses[i]}"` : "";

      row += `<${tag}${classAttr}>${cell}</${tag}>`;
    }

    return `${row}</tr>`;
  };

  // Construct the printable table (same shape as upstream buttons.print.js).
  const tableNode = dt.table(0).node() as HTMLTableElement;
  const body = data.body as unknown as Array<Array<string | null>>;
  let tableHtml = `<table class="${tableNode.className}">`;

  if (printConfig.header) {
    tableHtml += `<thead>${addRow(data.header, "th")}</thead>`;
  }
  tableHtml += "<tbody>";
  for (let i = 0; i < body.length; i += 1) {
    tableHtml += addRow(body[i], "td");
  }
  tableHtml += "</tbody>";
  if (printConfig.footer && data.footer) {
    tableHtml += `<tfoot>${addRow(data.footer, "th")}</tfoot>`;
  }
  tableHtml += "</table>";

  // Head: title + a copy of the page's <style>/<link> so the table keeps styling.
  let head = `<title>${exportInfo.title}</title>`;

  $("style, link").each(function copyStyleNode() {
    head += styleNodeToAbsolute(this as HTMLElement);
  });

  const bodyHtml = `<h1>${exportInfo.title}</h1>`
    + `<div>${exportInfo.messageTop || ""}</div>`
    + tableHtml
    + `<div>${exportInfo.messageBottom || ""}</div>`;

  // Render into a hidden iframe (NOT window.open) and print that context.
  const existing = document.getElementById(HIDDEN_IFRAME_ID);

  if (existing && existing.parentNode) {
    existing.parentNode.removeChild(existing);
  }

  const iframe = document.createElement("iframe");

  iframe.id = HIDDEN_IFRAME_ID;
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;

  if (!frameWindow) {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);

    return;
  }

  const doc = frameWindow.document;

  doc.open();
  doc.write(`<!DOCTYPE html><html><head>${head}</head>`
    + `<body class="dt-print-view">${bodyHtml}</body></html>`);
  doc.close();

  // Rewrite <img src> to absolute so images resolve inside the iframe.
  $("img", doc.body).each(function rewriteImageSrc() {
    const img = this as HTMLImageElement;

    img.setAttribute("src", toAbsoluteUrl(img.getAttribute("src") || ""));
  });

  if (typeof printConfig.customize === "function") {
    (printConfig.customize as unknown as (win: Window) => void)(frameWindow);
  }

  let cleaned = false;
  const cleanup = (): void => {
    if (cleaned) return;
    cleaned = true;
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  frameWindow.onafterprint = cleanup;

  // Allow stylesheets/images a moment to load, then print.
  frameWindow.setTimeout(() => {
    if (printConfig.autoPrint !== false) {
      frameWindow.focus();
      frameWindow.print();
    }
    // Fallback cleanup: onafterprint does not fire in every browser.
    frameWindow.setTimeout(cleanup, STYLE_LOAD_DELAY_MS);
  }, STYLE_LOAD_DELAY_MS);
};

const dataTableStatic = $.fn.dataTable as unknown as DataTableStaticWithPrint;
const printButton = dataTableStatic.ext.buttons.print;

if (printButton) {
  printButton.action = printViaIframe;
}

export default printViaIframe;
